import React, { forwardRef, ForwardRefRenderFunction, useCallback, useEffect, useMemo, useRef } from "react";
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { GrossSalaryChangeFormContainerProps } from "models/propTypes/wintSalaryPropTypes";
import { SalaryFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import GrossSalaryChangeForm from "./grossSalaryChangeForm";
import { useGrossSalaryChangeContext } from "../context/useGrossSalaryChangeContext";
import { useWintSalaryService } from "serviceHooks";
//#endregion
import * as Yup from "yup";
import { EnumHelper, LocalizationHelperInstance } from "utils/helpers";
import { WintSalaryEnums } from "models/enums";
import { SelectionItem } from "models/utilTypes/selectionTypes";

const GrossSalaryChangeFormContainer: ForwardRefRenderFunction<FormHandles, GrossSalaryChangeFormContainerProps> = (
  props,
  ref
) => {
  const { personName, personId, yearMonth, onFormValidationChange } = props;
  const { companyResult, loading, refetchWithCalculation, minimumTargetGrossSalaryAmount, isTaxTypeSelectionEnabled } =
    useGrossSalaryChangeContext();

  const formValidityRef = useRef(false);

  const handleFormValidationChange = useCallback(
    (isValid: boolean) => {
      formValidityRef.current = isValid;
      onFormValidationChange?.(!loading && isValid);
    },
    [loading, onFormValidationChange]
  );

  useEffect(() => {
    onFormValidationChange?.(!loading && formValidityRef.current);
  }, [loading, onFormValidationChange]);

  const { updateTargetGrossSalaryAmount } = useWintSalaryService();

  const initialValues: SalaryFormTypes.GrossSalaryChange = {
    grossSalary: undefined,
    taxType: WintSalaryEnums.ChangeGrossSalaryTaxType.OneTimeTax,
  };

  const availableTaxTypes: SelectionItem[] = useMemo(() => {
    return EnumHelper.getNumericItemValues(WintSalaryEnums.ChangeGrossSalaryTaxType).map((type) => ({
      id: type,
      value: LocalizationHelperInstance.getTranslatedEnumValue("Salary.ChangeGrossSalaryTaxType", type),
    }));
  }, []);

  const getMinimumAmount = (
    grossSalary: number | undefined,
    taxType?: WintSalaryEnums.ChangeGrossSalaryTaxType
  ): number => {
    const effectiveTaxType = isTaxTypeSelectionEnabled(grossSalary)
      ? taxType
      : WintSalaryEnums.ChangeGrossSalaryTaxType.TableTax;
    switch (effectiveTaxType) {
      case WintSalaryEnums.ChangeGrossSalaryTaxType.TableTax:
        return minimumTargetGrossSalaryAmount.taxTableMinimumAmount;
      case WintSalaryEnums.ChangeGrossSalaryTaxType.OneTimeTax:
        return minimumTargetGrossSalaryAmount.oneTimeTaxMinimumAmount;
      default:
        return minimumTargetGrossSalaryAmount.taxTableMinimumAmount;
    }
  };

  const validationSchema = Yup.object<SalaryFormTypes.GrossSalaryChange>({
    grossSalary: Yup.number()
      .required(TranslationKeys.Validations.Required)
      .test("min-by-tax-type", function (value) {
        const { taxType } = this.parent as SalaryFormTypes.GrossSalaryChange;
        const minAmount = getMinimumAmount(value, taxType);
        if (value !== undefined && value < minAmount) {
          return this.createError({
            message: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Validations.Min, "", {
              min: minAmount.toString(),
            }),
          });
        }
        return true;
      }),
    taxType: Yup.mixed()
      .oneOf(Object.values(WintSalaryEnums.ChangeGrossSalaryTaxType))
      .when("grossSalary", {
        is: (grossSalary: number | undefined) => isTaxTypeSelectionEnabled(grossSalary),
        then: (schema) => schema.required(TranslationKeys.Validations.Required),
      }),
  });

  const handleSubmit = async (values: SalaryFormTypes.GrossSalaryChange) => {
    if (loading) {
      return Promise.reject();
    }
    const targetSalaryAmount = Number(values.grossSalary ?? 0);
    const useOneTimeTax = isTaxTypeSelectionEnabled(targetSalaryAmount)
      ? values.taxType === WintSalaryEnums.ChangeGrossSalaryTaxType.OneTimeTax
      : undefined;
    return await updateTargetGrossSalaryAmount(personId, yearMonth, targetSalaryAmount, useOneTimeTax);
  };

  return (
    <GrossSalaryChangeForm
      ref={ref}
      {...props}
      defaultValues={initialValues}
      validationSchema={validationSchema}
      handleSubmit={handleSubmit}
      companyResult={companyResult}
      personName={personName}
      loading={loading}
      recalculateHandler={refetchWithCalculation}
      availableTaxTypes={availableTaxTypes}
      onFormValidationChange={handleFormValidationChange}
    />
  );
};

export default forwardRef(GrossSalaryChangeFormContainer);
